import React, { useState, useEffect } from 'react';
import { FiCalendar, FiFilter, FiX, FiClock, FiUser, FiPlus, FiEdit, FiTrash2 } from 'react-icons/fi';
import resourceAvailabilityService from '../../services/resourceAvailabilityService';
import userService from '../../services/userService';
import './ResourceAllocationList.css';
import { toast } from 'react-toastify';

const ResourceAvailabilityCalendar = () => {
  const [availability, setAvailability] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingAvailability, setEditingAvailability] = useState(null);
  const [formData, setFormData] = useState({
    user_id: '',
    date: '',
    available_hours: '',
    status: '',
    notes: ''
  });
  const [filters, setFilters] = useState({
    status: '',
    start_date: '',
    end_date: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    loadAvailability();
    loadStatistics();
  }, [filters, pagination.page]);

  const loadAvailability = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sort_by: 'date',
        sort_order: 'DESC',
        ...filters
      };

      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) delete params[key];
      });

      const data = await resourceAvailabilityService.getAll(params);
      setAvailability(data.data || []);
      setPagination(prev => ({
        ...prev,
        total: data.total || 0,
        pages: data.pages || 0
      }));
    } catch (error) {
      console.error('Error loading availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const data = await resourceAvailabilityService.getStatistics(params);
      setStatistics(data.data);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const usersData = await userService.getForDropdown();
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({
      status: '',
      start_date: '',
      end_date: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleOpenModal = (avail = null) => {
    loadUsers();
    if (avail) {
      setEditingAvailability(avail);
      setFormData({
        user_id: avail.user_id || '',
        date: avail.date ? avail.date.split('T')[0] : '',
        available_hours: avail.available_hours || '',
        status: avail.status || '',
        notes: avail.notes || ''
      });
    } else {
      setEditingAvailability(null);
      setFormData({
        user_id: '',
        date: '',
        available_hours: '',
        status: '',
        notes: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAvailability(null);
    setFormData({
      user_id: '',
      date: '',
      available_hours: '',
      status: '',
      notes: ''
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingAvailability) {
        await resourceAvailabilityService.update(editingAvailability.id, formData);
        // toast.success(error.response?.data?.message || 'Updated Successfully');

      } else {
        await resourceAvailabilityService.create(formData);
        // toast.success(error.response?.data?.message || 'Created Successfully');

      }
      handleCloseModal();
      loadAvailability();
      loadStatistics();
    } catch (error) {
      console.error('Error saving availability:', error);
      toast.error(error.response?.data?.message || 'Failed to save availability');

    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this availability record?')) return;

    try {
      await resourceAvailabilityService.delete(id);
      loadAvailability();
      loadStatistics();
    } catch (error) {
      console.error('Error deleting availability:', error);
      alert('Failed to delete availability');
    }
  };

  return (
    <div className="resource-allocation-list">
      {statistics && (
        <div className="statistics-cards">
          <div className="stat-card">
            <div className="stat-icon">
              <FiCalendar />
            </div>
            <div className="stat-content">
              <div className="stat-value">{statistics.total_records || 0}</div>
              <div className="stat-label">Total Records</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon success">
              <FiUser />
            </div>
            <div className="stat-content">
              <div className="stat-value">{statistics.by_status?.Available || 0}</div>
              <div className="stat-label">Available</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon warning">
              <FiUser />
            </div>
            <div className="stat-content">
              <div className="stat-value">{statistics.by_status?.['Partially Available'] || 0}</div>
              <div className="stat-label">Partially Available</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon info">
              <FiClock />
            </div>
            <div className="stat-content">
              <div className="stat-value">{statistics.average_available_hours || 0}h</div>
              <div className="stat-label">Avg Hours/Day</div>
            </div>
          </div>
        </div>
      )}

      <div className="list-header">
        <div className="header-left">
          <h2>Resource Availability </h2>
          <span className="count">{pagination.total} total</span>
        </div>
        <div className="header-right">
          <button className="btn btn-primary" onClick={() => handleOpenModal()}>
            <FiPlus /> Add Availability
          </button>
          <button className="btn-icon" onClick={() => setShowFilters(!showFilters)}>
            <FiFilter />
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="filters-panel">
          <div className="filters-grid">
            <div className="form-group">
              <label>Status</label>
              <select name="status" value={filters.status} onChange={handleFilterChange}>
                <option value="">All Status</option>
                <option value="Available">Available</option>
                <option value="Partially Available">Partially Available</option>
                <option value="Unavailable">Unavailable</option>
                <option value="On Leave">On Leave</option>
              </select>
            </div>
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                name="start_date"
                value={filters.start_date}
                onChange={handleFilterChange}
              />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                name="end_date"
                value={filters.end_date}
                onChange={handleFilterChange}
              />
            </div>
          </div>
          <div className="filters-actions">
            <button className="btn btn-secondary" onClick={handleClearFilters}>
              <FiX /> Clear Filters
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading availability...</div>
      ) : availability.length === 0 ? (
        <div className="empty-state">
          <FiCalendar size={48} />
          <h3>No Availability Records Found</h3>
          <p>No availability records match your current filters</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Date</th>
                  <th>Available Hours</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {availability.map(avail => (
                  <tr key={avail.id}>
                    <td>
                      <div className="user-cell">
                        <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                                                      {}
                            {avail.user?.avatar_url ? (
                              <img
                                src={avail.user.avatar_url}
                                alt={`${avail.user.first_name} ${avail.user.last_name}`}
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : (
                              <>
                                {avail.user?.first_name?.[0]}
                                {avail.user?.last_name?.[0]}
                              </>
                            )}
                        </div>
                        <div>
                          <div className="user-name">
                            {avail.user?.first_name} {avail.user?.last_name}
                          </div>
                          <div className="user-email">{avail.user?.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <strong>{resourceAvailabilityService.formatDate(avail.date)}</strong>
                    </td>
                    <td>
                      <span className="allocation-badge info flex items-center gap-1">
                        <FiClock /> {avail.available_hours}h
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${resourceAvailabilityService.getStatusColor(avail.status)}`} >
                        {avail.status}
                      </span>
                    </td>
                    <td>
                      <div className="notes-cell">
                        {avail.notes || '-'}
                      </div>
                    </td>
                    <td>
                      <div className=" flex">
                        <button
                          className="btn-icon btn-icon-primary"
                          onClick={() => handleOpenModal(avail)}
                          title="Edit"
                        >
                          <FiEdit className='text-blue-600 hover:text-blue-900'/>
                        </button>
                        <button
                          className="btn-icon btn-icon-danger"
                          onClick={() => handleDelete(avail.id)}
                          title="Delete"
                        >
                          <FiTrash2 className='text-red-600 hover:text-red-900'/>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-secondary"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                Previous
              </button>
              <span className="page-info">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                className="btn btn-secondary"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingAvailability ? 'Edit Availability' : 'Add New Availability'}</h2>
              <button className="btn-icon" onClick={handleCloseModal}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>User ID*</label>
                  <select
                    name="user_id"
                    value={formData.user_id}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="">Select User</option>
                    {users.map(user => (
                      <option key={user.value} value={user.value}>
                        {user.label} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleFormChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Available Hours *</label>
                  <input
                    type="number"
                    name="available_hours"
                    value={formData.available_hours}
                    onChange={handleFormChange}
                    required
                    min="0"
                    max="24"
                    step="0.5"
                    placeholder="e.g., 8"
                  />
                </div>
                <div className="form-group">
                  <label>Status *</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="">Select Status</option>
                    <option value="Available">Available</option>
                    <option value="Partially Available">Partially Available</option>
                    <option value="Unavailable">Unavailable</option>
                    <option value="On Leave">On Leave</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleFormChange}
                    rows="3"
                    placeholder="Add any additional notes..."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingAvailability ? 'Update Availability' : 'Add Availability'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourceAvailabilityCalendar;

// ---